import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  InputBase
} from '@mui/material'
import {
  Search as SearchIcon,
  Close,
  SwapVert
} from '@mui/icons-material'

const RELEVANCY_FILTERS = [
  { value: 'UNCHECKED', labelKey: 'mailTemplate.campaigns.dashboard.filter.unchecked', color: '#9E9E9E', type: 'status' },
  { value: 'HIGHLY_SUITABLE', level: '1', color: '#1B5E20', type: 'level' },
  { value: 'MODERATELY_SUITABLE', level: '2', color: '#388E3C', type: 'level' },
  { value: 'MINIMALLY_SUITABLE', level: '3', color: '#8BC34A', type: 'level' },
  { value: 'NOT_SUITABLE', level: 'X', color: '#F44336', type: 'level' },
  { value: 'CANNOT_ACCESS', level: 'N/A', color: '#660101', type: 'level' }
]

const CONTACT_FILTERS = [
  { value: 'NOT_STARTED', labelKey: 'mailTemplate.campaigns.dashboard.filter.notSearched', color: '#9E9E9E' },
  { value: 'DONE', labelKey: 'mailTemplate.campaigns.dashboard.filter.hasContacts', color: '#2E7D32' }
]

const CONTENT_FILTERS = [
  { value: 'NOT_STARTED', labelKey: 'mailTemplate.campaigns.dashboard.filter.notGenerated', color: '#9E9E9E' },
  { value: 'DONE', labelKey: 'mailTemplate.campaigns.dashboard.filter.ready', color: '#2E7D32' }
]

const SORT_OPTIONS = [
  { sortBy: 'FIRM_NAME', direction: 'ASC', labelKey: 'mailTemplate.campaigns.dashboard.sort.firmNameAsc' },
  { sortBy: 'FIRM_NAME', direction: 'DESC', labelKey: 'mailTemplate.campaigns.dashboard.sort.firmNameDesc' },
  { sortBy: null, direction: 'ASC', labelKey: 'mailTemplate.campaigns.dashboard.sort.relevancyAsc', isDefault: true }
]

const CampaignFilterToolbar = ({ filters, onFilterChange, sorting, onSortChange }) => {
  const { t } = useTranslation()
  const { search, relevancy, contactStatus, contentStatus } = filters

  const hasActiveFilters = search || relevancy.length > 0 || contactStatus.length > 0 || contentStatus.length > 0
  const toggleFilter = (key, value) => {
    const current = filters[key]
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]
    onFilterChange({ ...filters, [key]: next })
  }

  const clearAll = () => {
    onFilterChange({ search: '', relevancy: [], contactStatus: [], contentStatus: [] })
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 4,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flex: 1,
            maxWidth: 320,
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            transition: 'all 0.2s',
            border: '1px solid transparent',
            '&:focus-within': {
              backgroundColor: 'background.paper',
              borderColor: 'primary.main',
              boxShadow: '0 0 0 3px rgba(15, 198, 185, 0.1)'
            }
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <InputBase
            placeholder={t('mailTemplate.campaigns.dashboard.searchFirms')}
            value={search}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            sx={{
              flex: 1,
              fontSize: '0.85rem',
              '& input': { p: 0 },
              '& input::placeholder': { opacity: 0.5 }
            }}
          />
          {search && (
            <IconButton size="small" onClick={() => onFilterChange({ ...filters, search: '' })} sx={{ p: 0.25 }}>
              <Close sx={{ fontSize: 14, color: 'text.disabled' }} />
            </IconButton>
          )}
        </Box>

        {hasActiveFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
            {relevancy.map(v => {
              const f = RELEVANCY_FILTERS.find(sf => sf.value === v)
              if (!f) return null
              return (
                <Chip
                  key={`active-suit-${v}`}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {f.type === 'status'
                        ? (
                          <>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                            <span>{t(f.labelKey)}</span>
                          </>
                          )
                        : (
                          <>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: f.color, flexShrink: 0 }} />
                            <span>{f.level}</span>
                          </>
                          )
                      }
                    </Box>
                  }
                  size="small"
                  onDelete={() => toggleFilter('relevancy', v)}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-deleteIcon': { fontSize: 14, ml: 0 } }}
                />
              )
            })}
            {contactStatus.map(v => {
              const f = CONTACT_FILTERS.find(sf => sf.value === v)
              if (!f) return null
              return (
                <Chip
                  key={`active-contact-${v}`}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                      <span>{t('mailTemplate.campaigns.dashboard.filter.contacts')}: {t(f.labelKey)}</span>
                    </Box>
                  }
                  size="small"
                  onDelete={() => toggleFilter('contactStatus', v)}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-deleteIcon': { fontSize: 14, ml: 0 } }}
                />
              )
            })}
            {contentStatus.map(v => {
              const f = CONTENT_FILTERS.find(sf => sf.value === v)
              if (!f) return null
              return (
                <Chip
                  key={`active-content-${v}`}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                      <span>{t('mailTemplate.campaigns.dashboard.filter.content')}: {t(f.labelKey)}</span>
                    </Box>
                  }
                  size="small"
                  onDelete={() => toggleFilter('contentStatus', v)}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-deleteIcon': { fontSize: 14, ml: 0 } }}
                />
              )
            })}
          </Box>
        )}

        {!hasActiveFilters && <Box sx={{ flex: 1 }} />}

        {hasActiveFilters && (
          <Typography
            variant="caption"
            onClick={clearAll}
            sx={{
              color: 'text.secondary',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 500,
              '&:hover': { color: 'error.main', textDecoration: 'underline' }
            }}
          >
            {t('mailTemplate.campaigns.dashboard.clearAll')}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', fontWeight: 600, mr: 0.5 }}>
            {t('mailTemplate.campaigns.dashboard.filter.relevancy')}
          </Typography>
          {RELEVANCY_FILTERS.map((f) => {
            const isActive = relevancy.includes(f.value)
            return (
              <Chip
                key={f.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {f.type === 'status'
                      ? (
                        <>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                          <span>{t(f.labelKey)}</span>
                        </>
                        )
                      : (
                        <Box
                          sx={{
                            minWidth: 14,
                            height: 14,
                            px: f.level === 'N/A' ? 0.25 : 0,
                            borderRadius: '3px',
                            backgroundColor: isActive ? f.color : 'transparent',
                            border: `1.5px solid ${f.color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: f.level === 'N/A' ? '0.5rem' : '0.55rem',
                            fontWeight: 'bold',
                            color: isActive ? 'white' : f.color,
                            flexShrink: 0
                          }}
                        >
                          {f.level}
                        </Box>
                        )
                    }
                  </Box>
                }
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => toggleFilter('relevancy', f.value)}
                sx={{
                  height: 26,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: f.type === 'status' ? '0.7rem' : undefined,
                  borderColor: isActive ? f.color : 'divider',
                  backgroundColor: isActive ? `${f.color}14` : 'transparent',
                  color: isActive ? f.color : 'text.secondary',
                  '&:hover': { borderColor: f.color, backgroundColor: `${f.color}0A` },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )
          })}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, alignSelf: 'center' }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', fontWeight: 600, mr: 0.5 }}>
            {t('mailTemplate.campaigns.dashboard.filter.contacts')}
          </Typography>
          {CONTACT_FILTERS.map((f) => {
            const isActive = contactStatus.includes(f.value)
            return (
              <Chip
                key={`contact-${f.value}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                    <span>{t(f.labelKey)}</span>
                  </Box>
                }
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => toggleFilter('contactStatus', f.value)}
                sx={{
                  height: 26,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  borderColor: isActive ? f.color : 'divider',
                  backgroundColor: isActive ? `${f.color}14` : 'transparent',
                  color: isActive ? f.color : 'text.secondary',
                  '&:hover': { borderColor: f.color, backgroundColor: `${f.color}0A` },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )
          })}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, alignSelf: 'center' }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', fontWeight: 600, mr: 0.5 }}>
            {t('mailTemplate.campaigns.dashboard.filter.content')}
          </Typography>
          {CONTENT_FILTERS.map((f) => {
            const isActive = contentStatus.includes(f.value)
            return (
              <Chip
                key={`content-${f.value}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: f.color, flexShrink: 0 }} />
                    <span>{t(f.labelKey)}</span>
                  </Box>
                }
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => toggleFilter('contentStatus', f.value)}
                sx={{
                  height: 26,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  borderColor: isActive ? f.color : 'divider',
                  backgroundColor: isActive ? `${f.color}14` : 'transparent',
                  color: isActive ? f.color : 'text.secondary',
                  '&:hover': { borderColor: f.color, backgroundColor: `${f.color}0A` },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )
          })}
        </Box>

        {onSortChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, alignSelf: 'center' }} />

            <SwapVert sx={{ fontSize: 16, color: 'text.disabled', mr: 0.25 }} />
            {SORT_OPTIONS.map((opt) => {
              const isActive = opt.isDefault
                ? !sorting?.sortBy
                : sorting?.sortBy === opt.sortBy && sorting?.direction === opt.direction
              return (
                <Chip
                  key={`${opt.sortBy || 'default'}-${opt.direction}`}
                  label={t(opt.labelKey)}
                  size="small"
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => onSortChange({ sortBy: opt.sortBy, direction: opt.direction })}
                  sx={{
                    height: 26,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    backgroundColor: isActive ? 'rgba(15, 198, 185, 0.15)' : 'transparent',
                    color: isActive ? '#0aaa9f' : 'text.secondary',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(15, 198, 185, 0.06)' },
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CampaignFilterToolbar
